/**
 * SuperAdmin Dashboard Shell — Phase 8
 * Platform-level management for LifeChiAI superadmin role only.
 * Route: /superadmin (protected via ProtectedRoute in App.js — 'superadmin' only, never 'admin')
 *
 * Layout: 260px fixed left sidebar + scrollable main content.
 * Navigation: internal activePage state — no sub-routes needed.
 * Mobile: sidebar collapses behind hamburger at ≤768px.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiGrid, FiHome, FiUserPlus, FiUsers, FiDollarSign,
  FiActivity, FiCpu, FiSettings, FiLogOut, FiMenu, FiX, FiUser, FiAward, FiGlobe, FiBookOpen, FiTag, FiDownload, FiFileText,
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import Overview from '../components/superadmin/Overview';
import OnboardingQueue from '../components/superadmin/OnboardingQueue';
import SoloQueue from '../components/superadmin/SoloQueue';
import PoolQueue from '../components/superadmin/PoolQueue';
import PlaceholderPage from '../components/superadmin/PlaceholderPage';
import AICostMonitor  from '../components/superadmin/AICostMonitor';
import APICostMonitor from '../components/superadmin/APICostMonitor';
import Foundations    from '../components/superadmin/Foundations';
import DirectoryModeration from '../components/superadmin/DirectoryModeration';
import Finance              from '../components/superadmin/Finance';
import Users               from '../components/superadmin/Users';
import KnowledgeBaseEditor from '../components/KnowledgeBaseEditor';
import PricingEditor        from './PricingEditor';
import SuperAdminInvoices  from './SuperAdminInvoices';
import Centres              from '../components/superadmin/Centres';
import ReferralConfig      from '../components/superadmin/ReferralConfig';
import ReferralApprovals   from '../components/superadmin/ReferralApprovals';
import CentreInsights      from '../components/superadmin/CentreInsights';
import api, { getSoloApplications } from '../utils/api';

const NAV_ITEMS_BASE = [
  { key: 'overview',    label: 'Overview',        icon: FiGrid,       component: 'overview'    },
  { key: 'centres',     label: 'Centres',          icon: FiHome,       component: 'centres'     },
  { key: 'onboarding',  label: 'Onboarding queue', icon: FiUserPlus,   component: 'onboarding'  },
  { key: 'solo-queue',  label: 'Solo queue',       icon: FiUser,       component: 'solo-queue'  },
  { key: 'pool-queue',  label: 'Pool queue',       icon: FiUsers,      component: 'pool-queue'  },
  { key: 'foundations', label: 'Foundations',      icon: FiAward,      component: 'foundations' },
  { key: 'directory',   label: 'Directory',        icon: FiGlobe,      component: 'directory'   },
  { key: 'users',       label: 'Users',            icon: FiUsers,      component: 'users'       },
  { key: 'centre-insights', label: 'Centre Insights', icon: FiActivity,  component: 'centre-insights' },
  { key: 'finance',     label: 'Finance',          icon: FiDollarSign, component: 'finance'     },
  { key: 'invoices',   label: 'Invoices',         icon: FiFileText,   component: 'invoices'    },
  { key: 'api-costs',   label: 'API costs',         icon: FiActivity,   component: 'api-costs'   },
  { key: 'ai-monitor',     label: 'AI monitor',      icon: FiCpu,       component: 'ai-monitor'     },
  { key: 'knowledge-base', label: 'Knowledge Base',  icon: FiBookOpen,  component: 'knowledge-base' },
  { key: 'pricing',          label: 'Pricing',          icon: FiTag,        component: 'pricing'          },
  { key: 'referral-config', label: 'Referral config',  icon: FiTag,        component: 'referral-config'  },
  { key: 'referral-queue',  label: 'Referral queue',   icon: FiUserPlus,   component: 'referral-queue'   },
  { key: 'system',          label: 'System config',    icon: FiSettings,   component: 'placeholder'      },
];

const PAGE_TITLES = {
  'centre-insights': 'Centre Insights',
  overview:     'Overview',
  centres:      'Centres',
  onboarding:   'Onboarding queue',
  'solo-queue': 'Solo queue',
  'pool-queue': 'Pool Healer Approvals',
  foundations:  'Foundations',
  directory:    'Directory',
  users:        'Users',
  finance:      'Finance',
  invoices:     'Subscription Invoices',
  'api-costs':  'API Cost Monitor',
  'ai-monitor':     'AI Cost Monitor',
  'knowledge-base': 'Knowledge Base',
  pricing:            'Pricing',
  'referral-config':  'Referral Reward Config',
  'referral-queue':   'Referral Approval Queue',
  system:             'System config',
};

// ─── Styles ────────────────────────────────────────────────────────────────

const S = {
  shell: {
    display: 'flex', minHeight: '100vh',
    fontFamily: "'Raleway', 'Segoe UI', sans-serif",
    background: '#f4f6f4',
  },

  sidebar: (open, isMobile) => ({
    width: '260px',
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #071e12 0%, #0c2617 100%)',
    borderRight: '1px solid rgba(255,255,255,0.06)',
    display: 'flex', flexDirection: 'column',
    position: 'fixed', top: 0, left: 0, bottom: 0,
    zIndex: 200,
    transform: isMobile ? (open ? 'translateX(0)' : 'translateX(-260px)') : 'translateX(0)',
    transition: 'transform 0.25s ease',
    overflowY: 'auto',
  }),

  overlay: (open) => ({
    display: open ? 'block' : 'none',
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.45)',
    zIndex: 199,
  }),

  brand: {
    padding: '22px 20px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    display: 'flex', alignItems: 'center', gap: '12px',
  },

  brandAvatar: {
    width: '38px', height: '38px', borderRadius: '10px',
    background: 'linear-gradient(135deg, #1a6b5a, #4a8f82)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '15px', fontWeight: '800', color: '#fff',
    flexShrink: 0, letterSpacing: '0.5px',
  },

  brandName: { fontSize: '13px', fontWeight: '700', color: '#fff', letterSpacing: '0.3px' },
  brandSub:  { fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' },

  nav: { flex: 1, padding: '10px 0' },

  navItem: (active) => ({
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '12px 20px',
    cursor: 'pointer',
    background: active ? 'rgba(74,143,130,0.15)' : 'transparent',
    borderLeft: active ? '3px solid #4a8f82' : '3px solid transparent',
    transition: 'background 0.15s',
    minHeight: '44px',
  }),

  navIcon:  (active) => ({ fontSize: '17px', color: active ? '#4a8f82' : 'rgba(255,255,255,0.5)', flexShrink: 0 }),
  navLabel: (active) => ({ fontSize: '13px', fontWeight: active ? '700' : '500', color: active ? '#fff' : 'rgba(255,255,255,0.6)', flex: 1 }),

  navBadge: {
    background: '#e74c3c', color: '#fff',
    fontSize: '10px', fontWeight: '700',
    borderRadius: '9px', padding: '2px 6px',
    minWidth: '18px', textAlign: 'center',
  },

  logoutBtn: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '14px 20px',
    cursor: 'pointer', background: 'transparent', border: 'none',
    width: '100%', textAlign: 'left',
    borderTop: '1px solid rgba(255,255,255,0.07)',
    minHeight: '48px',
  },

  logoutIcon:  { fontSize: '17px', color: 'rgba(255,255,255,0.45)' },
  logoutLabel: { fontSize: '13px', fontWeight: '500', color: 'rgba(255,255,255,0.55)' },

  main: (isMobile) => ({
    flex: 1,
    marginLeft: isMobile ? '0' : '260px',
    display: 'flex', flexDirection: 'column',
    minHeight: '100vh',
  }),

  topBar: {
    background: '#fff',
    borderBottom: '1px solid #e8eae8',
    padding: '0 24px',
    height: '56px',
    display: 'flex', alignItems: 'center', gap: '12px',
    position: 'sticky', top: 0, zIndex: 100,
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  },

  hamburger: {
    background: 'transparent', border: 'none', cursor: 'pointer',
    padding: '8px', color: '#555', fontSize: '20px',
    display: 'flex', alignItems: 'center',
    minHeight: '44px', minWidth: '44px', justifyContent: 'center',
  },

  pageTitle: { flex: 1, fontSize: '15px', fontWeight: '700', color: '#1a1a2e' },

  saBadge: {
    background: 'rgba(26,107,90,0.12)', color: '#1a6b5a',
    fontSize: '11px', fontWeight: '700',
    borderRadius: '6px', padding: '3px 8px',
    border: '1px solid rgba(26,107,90,0.2)',
    letterSpacing: '0.3px',
    whiteSpace: 'nowrap',
  },

  content: { flex: 1, padding: '24px', overflowY: 'auto' },
};

// ─── Component ─────────────────────────────────────────────────────────────

const SuperAdminDashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [activePage,       setActivePage]       = useState('overview');
  const [sidebarOpen,      setSidebarOpen]      = useState(false);
  const [soloPendingCount, setSoloPendingCount] = useState(0);
  const [exportMonth,      setExportMonth]      = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
  });
  const [exportLoading,    setExportLoading]    = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await getSoloApplications('pending_verification');
        setSoloPendingCount(res.data.total || 0);
      } catch (err) {
        console.warn('SuperAdminDashboard: solo pending badge fetch failed', err.message);
      }
    })();
  }, []);

  const NAV_ITEMS = NAV_ITEMS_BASE.map(item =>
    item.key === 'solo-queue' ? { ...item, badge: soloPendingCount } : item
  );

  const isMobile = window.innerWidth <= 768;

  const handleNav = (key) => {
    setActivePage(key);
    if (isMobile) setSidebarOpen(false);
  };

  const handleGSTExport = async () => {
    // W1: regex guard matches server-side monthRange validation — rejects injected chars.
    if (exportLoading || !exportMonth || !/^\d{4}-\d{2}$/.test(exportMonth)) return;
    setExportLoading(true);
    try {
      // W3: use shared apiClient — auth header injected by interceptor, no localhost fallback.
      const res  = await api.get('/superadmin/invoices/export', {
        params:       { month: exportMonth },
        responseType: 'blob',
      });
      const url  = URL.createObjectURL(new Blob([res.data]));
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `LifeChiAI-GST-${exportMonth}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      // W2: log error detail before showing user-facing message.
      console.error('[SuperAdminDashboard] GST export failed:', err?.message);
      alert('Export failed. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const renderPage = () => {
    const item = NAV_ITEMS.find(n => n.key === activePage);
    if (!item) return null;
    if (item.component === 'overview')   return <Overview onNavigate={handleNav} />;
    if (item.component === 'centres')    return <Centres />;
    if (item.component === 'onboarding') return <OnboardingQueue />;
    if (item.component === 'solo-queue')  return <SoloQueue />;
    if (item.component === 'pool-queue')  return <PoolQueue />;
    if (item.component === 'foundations') return <Foundations />;
    if (item.component === 'directory')   return <DirectoryModeration />;
    if (item.component === 'api-costs')   return <APICostMonitor />;
    if (item.component === 'ai-monitor')  return <AICostMonitor />;
    if (item.component === 'finance') return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px',
                      background: '#fff', border: '1px solid #e8eae8', borderRadius: '10px',
                      padding: '14px 18px', flexWrap: 'wrap' }}>
          <FiDownload size={16} color="#1a6b5a" />
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e' }}>GST Invoice Export</span>
          <input
            type="month"
            value={exportMonth}
            onChange={e => setExportMonth(e.target.value)}
            style={{ border: '1px solid #ddd', borderRadius: '6px', padding: '6px 10px',
                     fontSize: '13px', color: '#1a1a2e', minHeight: '36px' }}
          />
          <button
            onClick={handleGSTExport}
            disabled={exportLoading || !exportMonth}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
                     borderRadius: '8px', border: 'none', background: '#1a6b5a', color: '#fff',
                     fontSize: '13px', fontWeight: '600',
                     cursor: (exportLoading || !exportMonth) ? 'default' : 'pointer',
                     opacity: (exportLoading || !exportMonth) ? 0.6 : 1, minHeight: '36px' }}
            type="button"
          >
            <FiDownload size={14} />
            {exportLoading ? 'Exporting…' : 'Download XLS'}
          </button>
        </div>
        <Finance />
      </>
    );
    if (item.component === 'invoices')        return <SuperAdminInvoices />;
    if (item.component === 'users')           return <Users />;
    if (item.component === 'centre-insights') return <CentreInsights />;
    if (item.component === 'knowledge-base')  return <KnowledgeBaseEditor />;
    if (item.component === 'pricing')          return <PricingEditor />;
    if (item.component === 'referral-config')  return <ReferralConfig />;
    if (item.component === 'referral-queue')   return <ReferralApprovals />;
    return <PlaceholderPage pageName={PAGE_TITLES[activePage]} />;
  };

  return (
    <div style={S.shell}>
      {/* Mobile overlay */}
      <div style={S.overlay(sidebarOpen && isMobile)} onClick={() => setSidebarOpen(false)} />

      {/* Sidebar */}
      <div style={S.sidebar(sidebarOpen, isMobile)}>
        <div style={S.brand}>
          <div style={S.brandAvatar}>LC</div>
          <div>
            <div style={S.brandName}>LifeChiAI Platform</div>
            <div style={S.brandSub}>Super Admin Console</div>
          </div>
        </div>

        <nav style={S.nav}>
          {NAV_ITEMS.map(({ key, label, icon: Icon, badge }) => (
            <div
              key={key}
              style={S.navItem(activePage === key)}
              onClick={() => handleNav(key)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleNav(key)}
            >
              <Icon style={S.navIcon(activePage === key)} />
              <span style={S.navLabel(activePage === key)}>{label}</span>
              {badge > 0 && <span style={S.navBadge}>{badge}</span>}
            </div>
          ))}
        </nav>

        <button style={S.logoutBtn} onClick={handleLogout} type="button">
          <FiLogOut style={S.logoutIcon} />
          <span style={S.logoutLabel}>Log out</span>
        </button>
      </div>

      {/* Main area */}
      <div style={S.main(isMobile)}>
        <div style={S.topBar}>
          {isMobile && (
            <button
              style={S.hamburger}
              onClick={() => setSidebarOpen(o => !o)}
              type="button"
              aria-label="Toggle menu"
            >
              {sidebarOpen ? <FiX /> : <FiMenu />}
            </button>
          )}
          <span style={S.pageTitle}>{PAGE_TITLES[activePage]}</span>
          <span style={S.saBadge}>superAdmin</span>
        </div>

        <div style={S.content}>
          {renderPage()}
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
